import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { A11yModule } from '@angular/cdk/a11y';

import { ClickOutsideModule } from 'ng-click-outside';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressBarModule } from 'primeng/progressbar';
import { ButtonModule } from 'primeng/button';

import { LocalizationModule } from '../../Core/Localization/localization.module';
import { CommonFormsModule } from '../../Common/Forms/forms.module';
import { MiscModule } from '../../Common/Misc/misc.module';
import { LayoutModule } from '../../Common/Layout/layout.module';
import { TagsModule } from '../../Common/Tags/tags.module';
import { PageBuilderModule } from '../../Common/PageBuilder/page-builder.module';
import { FilterModule } from '../../Common/Filter/filter.module';
import { ChartsModule } from '../../Common/Charts/charts.module';
import { CommonDialogsModule } from '../../Common/Dialogs/common-dialogs.module';
import { CommonBasicModule } from '../../Common/Basic/basic.module';
import { CoreDialogsModule } from '../../Core/Dialogs/dialogs.module';
import { TargetingSharedModule } from '../../Common/TargetingShared/targeting-shared.module';
import { AccountModule } from '../../Account/account.module';
import { MentionsModule } from '../../Common/Mentions/mentions.module';
import { SourcesModule } from '../../Common/Sources/sources.module';
import { SocialSharedModule } from '../../Social/Shared/social-shared.module';
import { CoverageOverTimeComponent } from './components/CoverageOverTime/coverage-over-time.component';
import { DonutChartComponent } from './components/DonutChart/donut-chart.component';
import { KeywordGroupSummaryComponent } from './components/keywordGroupSummary/keyword-group-summary.component';
import { MentionDeleteDialogComponent } from './components/MentionDeleteDialog/mention-delete-dialog.component';
import { MentionsSortOrderComponent } from './components/MentionsSortOrder/mentions-sort-order.component';
import { MonitoringBulkActionDialogComponent } from './components/MonitoringBulkActionDialog/monitoring-bulk-action-dialog.component';
import { MonitoringKeywordGroupComponent } from './components/MonitoringKeywordGroup/monitoring-keyword-group.component';
import { MonitoringKeywordsComponent } from './components/MonitoringKeywords/monitoring-keywords.component';
import { MonitoringKeywordsInputComponent } from './components/MonitoringKeywordsInput/monitoring-keywords-input.component';
import { MonitoringResultComponent } from './components/MonitoringResult/monitoring-result.component';
import { MonitoringResultChartsComponent } from './components/MonitoringResultsCharts/monitoring-result-charts.component';
import { MonitoringResultSummaryComponent } from './components/MonitoringResultSummary/monitoring-result-summary.component';
import { MonitoringSearchComponent } from './components/MonitoringSearch/monitoring-search.component';
import { RelatedConceptsComponent } from './components/RelatedConcepts/related-concepts.component';
import { SlideIfDirective } from './directives/slideIf/slide-if.directive';
import { MonitoringSearchLanguageSelectDialogService } from './services/monitoring-search-language-select-dialog.service';
import { MonitoringSearchRegionSelectDialogService } from './services/monitoring-search-region-select-dialog.service';
import { MercuryApi } from '../../api/mercury/mercury-api';
import { OEmbedApi } from '../../api/oembed/oembed.api';
import { SaveTopicDialogComponent } from './components/SaveTopicDialog/save-topic-dialog.component';
import { TopicWorkflowSettingsComponent } from './components/TopicWorkflowSettings/topic-workflow-settings.component';
import { MonitoringExpertBooleanSearchComponent } from './components/MonitoringExpertBooleanSearch/monitoring-expert-boolean-search.component';
import { MonitoringSearchContextsComponent } from './components/MonitoringSearchContexts/monitoring-search-contexts.component';
import { BulkActionFormExportComponent } from './components/BulkActionFormExport/bulk-action-form-export.component';
import { BulkActionFormTagComponent } from './components/BulkActionFormTag/bulk-action-form-tag.component';
import { BulkActionFormSingleTagComponent } from './components/BulkActionFormSingleTag/bulk-action-form-single-tag.component';
import { BulkActionFormSentimentComponent } from './components/BulkActionFormSentiment/bulk-action-form-sentiment.component';
import { BulkActionFormAccountComponent } from './components/BulkActionFormAccount/bulk-action-form-account.component';
import { BulkActionFormFolderComponent } from './components/BulkActionFormFolder/bulk-action-form-folder.component';
import { BulkActionFormBlankComponent } from './components/BulkActionFormBlank/bulk-action-form-blank.component';
import { BulkActionFormRenormalizeComponent } from './components/BulkActionFormRenormalize/bulk-action-form-renormalize.component';
import { DataSelectDialogComponent } from '../../Common/Dialogs/components/DataSelectDialog/data-select-dialog.component';
import { TagFilterCheckboxesComponent } from './components/TagFilterCheckboxes/tag-filter-checkboxes.component';
import { TopicBackfillDialogComponent } from './components/TopicBackfillDialog/topic-backfill-dialog.component';
import { SyndicatedMentionsTakeOverPanelComponent } from './components/SyndicatedMentionsTakeOverPanel/syndicated-mentions-take-over-panel.component';
import { EditTopicTakeOverPanelComponent } from './components/EditTopicTakeOverPanel/edit-topic-take-over-panel.component';
import { RSSFeedManageDialogComponent } from './components/RSSFeedManageDialog/rss-feed-manage-dialog.component';
import { RSSFeedCreateDialogComponent } from './components/RSSFeedCreateDialog/rss-feed-create-dialog.component';
import { RSSFeedPickerComponent } from './components/RSSFeedPicker/rss-feed-picker.component';
import { RSSFeedService } from './services/rss-feed.service';
import { RSSFeedApiService } from '../../api/rssFeed/rss-feed.api.service';
import { ProminenceFilterComponent } from './components/ProminenceFilter/prominence-filter.component';
import { AgentsFilterComponent } from './components/AgentsFilter/agents-filter.component';
import { MentionImporterComponent } from './components/MentionImporter/mention-importer.component';
import { MentionImportConfirmationComponent } from './components/MentionImporter/MentionImportConfirmation/mention-import-confirmation.component';
import { MentionSearchResultComponent } from './components/MonitoringResult/mentionSearchResult/mention-search-result.component';
import { MonitoringKeywordFilterComponent } from './components/KeywordFilter/filter-keyword.component';
import { QuotaUsageOverTimeChartComponent } from './components/QuotaUsageOverTimeChart/quota-usage-over-time-chart.component';
import { MediaTypeCheckboxComponent } from './components/MediaTypeCheckbox/media-type-checkbox.component';
import { BriefingDateStyleFieldComponent } from './components/BriefingDateStyleField/briefing-date-style-field.component';
import { BriefingTextStyleFieldComponent } from './components/BriefingTextStyleField/briefing-text-style-field.component';
import { BriefingTitleStyleFieldComponent } from './components/BriefingTitleStyleField/briefing-title-style-field.component';
import { MentionCustomizationFieldComponent } from './components/MentionCustomizationField/mention-customization-field.component';
import { MonitoringSourcePickerFieldWithFacetComponent } from './components/MonitoringSourcePickerField/monitoring-source-picker-field-with-facet.component';
import {
    MonitoringSourcePickerFieldWithoutFacetComponent
} from './components/MonitoringSourcePickerField/monitoring-source-picker-field-without-facet.component';
import {
    AnalysisBlockMonitoringSourcePickerFieldComponent
} from './components/MonitoringSourcePickerField/analysis-block-monitoring-source-picker-field.component';
import { RhpContextsModule } from '../../Common/RhpContexts/rhp-contexts.module';
import { EmailHeaderComponent } from './components/EmailHeader/email.header.component';
import { TakeOverModule } from '../../Common/TakeOver/take-over.module';
import { CopyMentionsToAccountService } from './services/copy-mentions-to-account.service';
import { ElementOverflowParentModule } from '../../Common/Forms/directives/elementOverflowParent/element-overflow-parent.module';
import { BriefingInsightUpsellCardComponent } from './components/BriefingInsightUpsellCard/briefing-insight-upsell-card.component';
import { SavedFilterManageDialogComponent } from './components/SavedFilterManageDialog/saved-filter-manage-dialog.component';
import { SavedFilterSetupDialogComponent } from './components/SavedFilterSetupDialog/saved-filter-setup-dialog.component';
import { AddMentionsWidgetComponent } from './components/AddMentionsWidget/add-mentions-widget.component';
import { MentionSavingUsageDashboardComponent } from './components/MentionSavingUsageDashboard/mention-saving-usage-dashboard.component';
import { MentionSavingUsageDashboardService } from './services/mention-saving-usage-dashboard.service';
import { MentionSavingLimitExceededDialogComponent } from './components/MentionSavingLimitExceededDialog/mention-saving-limit-exceeded-dialog.component';
import { MonitoringAnalyticsDashboardComponent } from './components/MonitoringAnalyticsDashboard/monitoring-analytics-dashboard.component';
import { MonitoringAnalyticsDashboardService } from './services/monitoring-analytics-dashboard.service';
import { MADSummaryComponent } from './components/MonitoringAnalyticsDashboard/MADSummary/mad-summary.component';
import { MADSentimentComponent } from './components/MonitoringAnalyticsDashboard/MADSentiment/mad-sentiment.component';
import { MADSummaryService } from './components/MonitoringAnalyticsDashboard/MADSummary/mad-summary.service';
import { MADSocialComponent } from './components/MonitoringAnalyticsDashboard/MADSocial/mad-social.component';
import { MADCoverageInsightsComponent } from './components/MonitoringAnalyticsDashboard/MADCoverageInsights/mad-coverageInsights.component';
import { TopMentionsMADChart } from './components/TopMentionsMAD/top-mentions-mad-chart.component';
import { MADChartsService } from './services/mad-charts.service';
import { MADSentimentService } from './components/MonitoringAnalyticsDashboard/MADSentiment/mad-sentiment.service';
import { MADSocialService } from './components/MonitoringAnalyticsDashboard/MADSocial/mad-social.service';
import { MentionVolumeEstimateDialogComponent } from './components/MentionVolumeEstimateDialog/mention-volume-estimate-dialog.component';
import { TopEntitiesChartBlockComponent } from './components/TopEntitiesChartBlock/top-entities-chart-block.component';
import { MonitoringSourcePickerFieldWithFacetAndSearchComponent }
    from './components/MonitoringSourcePickerField/monitoring-source-picker-field-with-facet-and-search.component';
import { BriefingSourcePickerFieldComponent } from './components/MonitoringSourcePickerField/briefing-source-picker-field.component';
import { DigestPickerComponent } from './components/DigestPicker/digest-picker.component';
import { ManageDigestsDialogComponent } from './components/ManageDigestDialog/manage-digests-dialog.component';
import { DigestSubscriberDialogComponent } from './components/DigestSubscriberDialog/digest-subscriber-dialog.component';
import { DigestShareDialogComponent } from './components/DigestShareDialog/digest-share-dialog.component';
import { MailingListsModule } from '@Common/MailingLists/mailing-lists.module';
import { MonitoringBrandCardComponent } from './components/MonitoringBrandCard/monitoring-brand-card.component';
import { PrecisionClipSetupDialogComponent } from './components/PrecisionClipSetupDialog/precision-clip-setup-dialog.component';
import { PrecisionClipLoadingDialogComponent } from './components/PrecisionClipLoadingDialog/precision-clip-loading-dialog.component';

export const monitorSharedImports = [
    LocalizationModule,
    CommonFormsModule,
    TagsModule,
    SourcesModule,
    MiscModule,
    LayoutModule,
    CommonModule,
    FormsModule,
    SelectModule,
    TooltipModule,
    AutoCompleteModule,
    InputTextModule,
    MultiSelectModule,
    CommonDialogsModule,
    ChartsModule,
    DatePickerModule,
    ClickOutsideModule,
    PageBuilderModule,
    FilterModule,
    TextareaModule,
    CommonBasicModule,
    CoreDialogsModule,
    ToggleSwitchModule,
    ProgressBarModule,
    TargetingSharedModule,
    AccountModule,
    MentionsModule,
    RouterModule,
    RhpContextsModule,
    A11yModule,
    ReactiveFormsModule,
    TakeOverModule,
    ElementOverflowParentModule,
    SocialSharedModule,
    ButtonModule,
    MailingListsModule
];

export const monitorSharedDeclarations = [
    CoverageOverTimeComponent,
    DonutChartComponent,
    KeywordGroupSummaryComponent,
    MediaTypeCheckboxComponent,
    MentionDeleteDialogComponent,
    MentionsSortOrderComponent,
    MonitoringBulkActionDialogComponent,
    MonitoringExpertBooleanSearchComponent,
    MonitoringSearchContextsComponent,
    MonitoringKeywordGroupComponent,
    MonitoringKeywordsComponent,
    MonitoringKeywordsInputComponent,
    MonitoringResultComponent,
    MonitoringResultChartsComponent,
    MonitoringResultSummaryComponent,
    MonitoringSearchComponent,
    MonitoringSourcePickerFieldWithFacetComponent,
    MonitoringSourcePickerFieldWithFacetAndSearchComponent,
    MonitoringSourcePickerFieldWithoutFacetComponent,
    AnalysisBlockMonitoringSourcePickerFieldComponent,
    RelatedConceptsComponent,
    SlideIfDirective,
    SaveTopicDialogComponent,
    TopicWorkflowSettingsComponent,
    BulkActionFormExportComponent,
    BulkActionFormTagComponent,
    BulkActionFormSingleTagComponent,
    BulkActionFormSentimentComponent,
    BulkActionFormFolderComponent,
    BulkActionFormAccountComponent,
    BulkActionFormBlankComponent,
    BulkActionFormRenormalizeComponent,
    TagFilterCheckboxesComponent,
    TopicBackfillDialogComponent,
    SyndicatedMentionsTakeOverPanelComponent,
    EditTopicTakeOverPanelComponent,
    RSSFeedManageDialogComponent,
    RSSFeedCreateDialogComponent,
    RSSFeedPickerComponent,
    ProminenceFilterComponent,
    AgentsFilterComponent,
    MonitoringKeywordFilterComponent,
    MentionImporterComponent,
    MentionImportConfirmationComponent,
    MentionSearchResultComponent,
    QuotaUsageOverTimeChartComponent,
    BriefingDateStyleFieldComponent,
    BriefingTextStyleFieldComponent,
    BriefingTitleStyleFieldComponent,
    MentionCustomizationFieldComponent,
    EmailHeaderComponent,
    BriefingInsightUpsellCardComponent,
    SavedFilterManageDialogComponent,
    SavedFilterSetupDialogComponent,
    AddMentionsWidgetComponent,
    MentionSavingUsageDashboardComponent,
    MentionVolumeEstimateDialogComponent,
    MentionSavingLimitExceededDialogComponent,
    MonitoringAnalyticsDashboardComponent,
    MADSummaryComponent,
    MADSentimentComponent,
    MADSocialComponent,
    MADCoverageInsightsComponent,
    TopMentionsMADChart,
    TopEntitiesChartBlockComponent,
    BriefingSourcePickerFieldComponent,
    DigestPickerComponent,
    ManageDigestsDialogComponent,
    DigestSubscriberDialogComponent,
    DigestShareDialogComponent,
    MonitoringBrandCardComponent,
    PrecisionClipSetupDialogComponent,
    PrecisionClipLoadingDialogComponent
];

@NgModule({
    imports: [
        ...monitorSharedImports
    ],
    exports: [
        CoverageOverTimeComponent,
        DonutChartComponent,
        KeywordGroupSummaryComponent,
        MediaTypeCheckboxComponent,
        MentionDeleteDialogComponent,
        MentionsSortOrderComponent,
        MonitoringBulkActionDialogComponent,
        MonitoringExpertBooleanSearchComponent,
        MonitoringSearchContextsComponent,
        MonitoringKeywordGroupComponent,
        MonitoringKeywordsComponent,
        MonitoringKeywordsInputComponent,
        MonitoringResultComponent,
        MonitoringResultChartsComponent,
        MonitoringResultSummaryComponent,
        MonitoringSearchComponent,
        MonitoringSourcePickerFieldWithFacetComponent,
        MonitoringSourcePickerFieldWithFacetAndSearchComponent,
        MonitoringSourcePickerFieldWithoutFacetComponent,
        AnalysisBlockMonitoringSourcePickerFieldComponent,
        RelatedConceptsComponent,
        SlideIfDirective,
        SaveTopicDialogComponent,
        TopicWorkflowSettingsComponent,
        DataSelectDialogComponent,
        TopicBackfillDialogComponent,
        RSSFeedManageDialogComponent,
        RSSFeedCreateDialogComponent,
        RSSFeedPickerComponent,
        MentionSearchResultComponent,
        QuotaUsageOverTimeChartComponent,
        BriefingDateStyleFieldComponent,
        BriefingTextStyleFieldComponent,
        BriefingTitleStyleFieldComponent,
        MentionCustomizationFieldComponent,
        EmailHeaderComponent,
        BriefingInsightUpsellCardComponent,
        SavedFilterManageDialogComponent,
        SavedFilterSetupDialogComponent,
        AddMentionsWidgetComponent,
        MonitoringKeywordFilterComponent,
        TagFilterCheckboxesComponent,
        MentionSavingUsageDashboardComponent,
        MentionVolumeEstimateDialogComponent,
        MentionSavingLimitExceededDialogComponent,
        MonitoringAnalyticsDashboardComponent,
        MADSummaryComponent,
        MADSentimentComponent,
        MADSocialComponent,
        TopEntitiesChartBlockComponent,
        BriefingSourcePickerFieldComponent,
        DigestPickerComponent,
        ManageDigestsDialogComponent,
        DigestSubscriberDialogComponent,
        DigestShareDialogComponent,
        MonitoringBrandCardComponent,
        PrecisionClipSetupDialogComponent,
        PrecisionClipLoadingDialogComponent
    ],
    declarations: [
        ...monitorSharedDeclarations
    ],
    providers: [
        MercuryApi,
        OEmbedApi,
        MonitoringSearchLanguageSelectDialogService,
        MonitoringSearchRegionSelectDialogService,
        CopyMentionsToAccountService,
        RSSFeedService,
        RSSFeedApiService,
        BriefingDateStyleFieldComponent,
        MentionSavingUsageDashboardService,
        MonitoringAnalyticsDashboardService,
        MADChartsService,
        MADSummaryService,
        MADSentimentService,
        MADSocialService,
    ]
})
export class MonitorSharedModule { }
